/**
 * Offline Data Service
 * Manages local storage for offline data access and sync
 */

const OFFLINE_DB_NAME = 'forgetmenot_offline';
const SYNC_QUEUE_KEY = 'offline_sync_queue';
const LAST_SYNC_KEY = 'last_sync_timestamp';
const CONFLICTS_KEY = 'offline_conflicts';

// Data stores for different types
const STORES = {
  documents: 'offline_documents',
  contacts: 'offline_contacts',
  calendar: 'offline_calendar',
  parking: 'offline_parking',
  medical: 'offline_medical',
  dates: 'offline_dates',
};

/**
 * Get data from local storage
 */
export const getOfflineData = (storeKey) => {
  try {
    const data = localStorage.getItem(STORES[storeKey] || storeKey);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error reading offline data:', error);
    return null;
  }
};

/**
 * Save data to local storage
 */
export const saveOfflineData = (storeKey, data) => {
  try {
    localStorage.setItem(STORES[storeKey] || storeKey, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving offline data:', error);
    // Handle quota exceeded
    if (error.name === 'QuotaExceededError') {
      clearOldOfflineData();
      try {
        localStorage.setItem(STORES[storeKey] || storeKey, JSON.stringify(data));
        return true;
      } catch (e) {
        return false;
      }
    }
    return false;
  }
};

/**
 * Clear old offline data to make space
 */
const clearOldOfflineData = () => {
  const keysToCheck = Object.values(STORES);
  keysToCheck.forEach(key => {
    const data = localStorage.getItem(key);
    if (data) {
      try {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed) && parsed.length > 100) {
          // Keep only last 50 items
          localStorage.setItem(key, JSON.stringify(parsed.slice(-50)));
        }
      } catch (e) {
        // Invalid data, remove it
        localStorage.removeItem(key);
      }
    }
  });
};

/**
 * Get the sync queue (pending changes)
 */
export const getSyncQueue = () => {
  try {
    const queue = localStorage.getItem(SYNC_QUEUE_KEY);
    return queue ? JSON.parse(queue) : [];
  } catch (error) {
    return [];
  }
};

/**
 * Add an operation to the sync queue
 */
export const addToSyncQueue = (operation) => {
  const queue = getSyncQueue();
  const queueItem = {
    id: `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    retryCount: 0,
    ...operation,
  };
  queue.push(queueItem);
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  return queueItem.id;
};

/**
 * Remove an operation from the sync queue
 */
export const removeFromSyncQueue = (operationId) => {
  const queue = getSyncQueue();
  const filtered = queue.filter(op => op.id !== operationId);
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(filtered));
};

/**
 * Clear the entire sync queue
 */
export const clearSyncQueue = () => {
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify([]));
};

/**
 * Get last sync timestamp
 */
export const getLastSyncTime = () => {
  return localStorage.getItem(LAST_SYNC_KEY);
};

/**
 * Update last sync timestamp
 */
export const updateLastSyncTime = () => {
  localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());
};

/**
 * Merge server data with local data (conflict resolution)
 * Strategy: Server wins for same items, keep local-only items
 */
export const mergeData = (serverData, localData, idKey = 'id') => {
  if (!localData || !Array.isArray(localData)) return serverData;
  if (!serverData || !Array.isArray(serverData)) return localData;
  
  const serverMap = new Map(serverData.map(item => [item[idKey], item]));
  const merged = [...serverData];
  
  // Add local-only items (items that don't exist on server)
  localData.forEach(localItem => {
    if (localItem._isLocalOnly && !serverMap.has(localItem[idKey])) {
      merged.push(localItem);
    }
  });
  
  return merged;
};

/**
 * Create a local-only item (for offline creation)
 */
export const createLocalItem = (data, type) => {
  const localId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  return {
    ...data,
    id: localId,
    _isLocalOnly: true,
    _localCreatedAt: new Date().toISOString(),
    _type: type,
  };
};

/**
 * Check if an item is local-only
 */
export const isLocalOnlyItem = (item) => {
  return item && (item._isLocalOnly === true || (item.id && item.id.startsWith('local_')));
};

/**
 * Offline-first data fetcher
 * Tries network first, falls back to cache, caches successful responses
 */
export const fetchWithOfflineSupport = async (fetchFn, cacheKey, options = {}) => {
  const { forceNetwork = false, cacheDuration = 5 * 60 * 1000 } = options;
  
  // Check cache first if not forcing network
  if (!forceNetwork && navigator.onLine === false) {
    const cached = getOfflineData(cacheKey);
    if (cached) {
      return { data: cached.data, fromCache: true, timestamp: cached.timestamp };
    }
  }
  
  try {
    // Try network request
    const response = await fetchFn();
    const data = response.data || response;
    
    // Cache the response
    saveOfflineData(cacheKey, {
      data,
      timestamp: new Date().toISOString(),
    });
    
    return { data, fromCache: false, timestamp: new Date().toISOString() };
  } catch (error) {
    // Network failed, try cache
    const cached = getOfflineData(cacheKey);
    if (cached) {
      console.log(`Network failed, using cached data for ${cacheKey}`);
      return { data: cached.data, fromCache: true, timestamp: cached.timestamp, error };
    }
    throw error;
  }
};

/**
 * Queue an offline write operation
 */
export const queueOfflineWrite = (type, method, endpoint, data) => {
  return addToSyncQueue({
    type,
    method,
    endpoint,
    data,
    status: 'pending',
  });
};

/**
 * Get sync status
 */
export const getSyncStatus = () => {
  const queue = getSyncQueue();
  const lastSync = getLastSyncTime();
  
  return {
    pendingOperations: queue.length,
    hasPendingChanges: queue.length > 0,
    lastSyncTime: lastSync,
    queue: queue.map(op => ({
      id: op.id,
      type: op.type,
      method: op.method,
      timestamp: op.timestamp,
      status: op.status,
    })),
  };
};

/**
 * Clear all offline data for a specific store
 */
export const clearOfflineStore = (storeKey) => {
  localStorage.removeItem(STORES[storeKey] || storeKey);
};

/**
 * Clear all offline data
 */
export const clearAllOfflineData = () => {
  Object.values(STORES).forEach(key => localStorage.removeItem(key));
  localStorage.removeItem(SYNC_QUEUE_KEY);
  localStorage.removeItem(LAST_SYNC_KEY);
  localStorage.removeItem(CONFLICTS_KEY);
};

/**
 * Get all detected conflicts
 */
export const getConflicts = () => {
  try {
    const conflicts = localStorage.getItem(CONFLICTS_KEY);
    return conflicts ? JSON.parse(conflicts) : [];
  } catch (error) {
    return [];
  }
};

/**
 * Add a conflict for resolution
 */
export const addConflict = (conflict) => {
  const conflicts = getConflicts();
  const conflictItem = {
    id: `conflict_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    detectedAt: new Date().toISOString(),
    resolved: false,
    ...conflict,
  };
  conflicts.push(conflictItem);
  localStorage.setItem(CONFLICTS_KEY, JSON.stringify(conflicts));
  return conflictItem.id;
};

/**
 * Resolve a specific conflict
 */
export const resolveConflict = (conflictId, strategy = 'server_wins') => {
  const conflicts = getConflicts();
  const conflict = conflicts.find(c => c.id === conflictId);
  
  if (!conflict) return null;
  
  // Mark as resolved
  conflict.resolved = true;
  conflict.resolvedAt = new Date().toISOString();
  conflict.strategy = strategy;
  
  // Update conflicts list
  localStorage.setItem(CONFLICTS_KEY, JSON.stringify(conflicts));
  
  // Return the winning data based on strategy
  return strategy === 'client_wins' ? conflict.local : conflict.server;
};

/**
 * Resolve all conflicts with a single strategy
 */
export const resolveAllConflicts = (strategy = 'server_wins') => {
  const conflicts = getConflicts();
  const results = [];
  
  conflicts.forEach(conflict => {
    if (!conflict.resolved) {
      conflict.resolved = true;
      conflict.resolvedAt = new Date().toISOString();
      conflict.strategy = strategy;
      results.push({
        id: conflict.id,
        data: strategy === 'client_wins' ? conflict.local : conflict.server
      });
    }
  });
  
  localStorage.setItem(CONFLICTS_KEY, JSON.stringify(conflicts));
  return results;
};

/**
 * Clear resolved conflicts (cleanup)
 */
export const clearResolvedConflicts = () => {
  const conflicts = getConflicts();
  const pending = conflicts.filter(c => !c.resolved);
  localStorage.setItem(CONFLICTS_KEY, JSON.stringify(pending));
};

/**
 * Get unresolved conflicts count
 */
export const getUnresolvedConflictsCount = () => {
  const conflicts = getConflicts();
  return conflicts.filter(c => !c.resolved).length;
};

/**
 * Detect conflict between local and server data
 */
export const detectConflict = (localItem, serverItem, idKey = 'id') => {
  if (!localItem || !serverItem) return false;
  
  // Check if local has unsaved changes
  if (!localItem._localUpdatedAt) return false;
  
  // Check if server was updated after local changes were made
  const localTime = new Date(localItem._localUpdatedAt).getTime();
  const serverTime = new Date(serverItem.updated_at || serverItem.updatedAt || 0).getTime();
  
  // If server was updated more recently and local has changes, it's a conflict
  if (serverTime > localTime && localItem._hasLocalChanges) {
    return true;
  }
  
  // Compare actual content
  const localCopy = { ...localItem };
  const serverCopy = { ...serverItem };
  
  // Remove metadata fields for comparison
  ['_localUpdatedAt', '_hasLocalChanges', '_isLocalOnly', 'updated_at', 'updatedAt', 'created_at', 'createdAt'].forEach(key => {
    delete localCopy[key];
    delete serverCopy[key];
  });
  
  return JSON.stringify(localCopy) !== JSON.stringify(serverCopy);
};

export default {
  getOfflineData,
  saveOfflineData,
  getSyncQueue,
  addToSyncQueue,
  removeFromSyncQueue,
  clearSyncQueue,
  getLastSyncTime,
  updateLastSyncTime,
  mergeData,
  createLocalItem,
  isLocalOnlyItem,
  fetchWithOfflineSupport,
  queueOfflineWrite,
  getSyncStatus,
  clearOfflineStore,
  clearAllOfflineData,
  getConflicts,
  addConflict,
  resolveConflict,
  resolveAllConflicts,
  clearResolvedConflicts,
  getUnresolvedConflictsCount,
  detectConflict,
};
